// src/pages/customer-panel/achievements/AchievementsPage.js
// Student achievements / badges page

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Avatar,
    Paper,
    Chip,
    CircularProgress,
    Alert,
    Button,
    Skeleton,
    Tooltip,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#10b981';

const BADGE_COLORS = [
    { bg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', text: '#f59e0b' },
    { bg: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', text: '#3b82f6' },
    { bg: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', text: '#8b5cf6' },
    { bg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', text: '#10b981' },
    { bg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', text: '#ef4444' },
    { bg: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)', text: '#ec4899' },
    { bg: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', text: '#06b6d4' },
    { bg: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)', text: '#f97316' },
];

function getBadgeColor(index) {
    return BADGE_COLORS[index % BADGE_COLORS.length];
}

function formatDate(dateStr) {
    if (!dateStr) return '';
    try {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return dateStr;
    }
}

function getBadgeLetter(badge) {
    const name = badge.name || badge.title || badge.badge_name || '';
    return name.charAt(0).toUpperCase() || 'B';
}

function isThisMonth(dateStr) {
    if (!dateStr) return false;
    try {
        const d = new Date(dateStr);
        const now = new Date();
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    } catch {
        return false;
    }
}

const StatCard = ({ icon: Icon, title, value, color }) => (
    <Card
        sx={{
            height: '100%',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: 'none',
            transition: 'all 0.2s',
            '&:hover': { borderColor: color, boxShadow: `0 4px 20px ${color}20` },
        }}
    >
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: '14px',
                        bgcolor: `${color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Icon sx={{ color, fontSize: 28 }} />
                </Box>
                <Box>
                    <Typography variant="body2" sx={{ fontFamily, color: '#64748b', fontWeight: 500 }}>
                        {title}
                    </Typography>
                    <Typography variant="h5" sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>
                        {value}
                    </Typography>
                </Box>
            </Box>
        </CardContent>
    </Card>
);

const BadgeCard = ({ badge, index }) => {
    const colorScheme = getBadgeColor(index);
    const letter = getBadgeLetter(badge);
    const earnedDate = badge.earned_date || badge.earned_at || badge.created_at;
    const name = badge.name || badge.title || badge.badge_name || 'Badge';
    const description = badge.description || badge.badge_description || '';
    const category = badge.category || badge.badge_category;
    const points = badge.points || badge.xp || null;

    return (
        <Card
            sx={{
                height: '100%',
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
                    borderColor: colorScheme.text,
                },
            }}
        >
            <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                {/* Badge icon */}
                <Tooltip title={name}>
                    <Avatar
                        sx={{
                            width: 72,
                            height: 72,
                            mx: 'auto',
                            mb: 2,
                            background: colorScheme.bg,
                            fontSize: '1.8rem',
                            fontFamily,
                            fontWeight: 800,
                            boxShadow: `0 8px 24px ${colorScheme.text}30`,
                        }}
                    >
                        {badge.icon ? (
                            <Box component="span" sx={{ fontSize: '2rem' }}>{badge.icon}</Box>
                        ) : (
                            letter
                        )}
                    </Avatar>
                </Tooltip>

                {/* Badge name */}
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontFamily,
                        fontWeight: 700,
                        color: '#1e293b',
                        mb: 0.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                >
                    {name}
                </Typography>

                {/* Description */}
                {description && (
                    <Typography
                        variant="body2"
                        sx={{
                            fontFamily,
                            color: '#64748b',
                            mb: 1.5,
                            fontSize: '0.8rem',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            lineHeight: 1.5,
                        }}
                    >
                        {description}
                    </Typography>
                )}

                {/* Category chip */}
                {category && (
                    <Chip
                        label={category}
                        size="small"
                        sx={{
                            fontFamily,
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            bgcolor: `${colorScheme.text}12`,
                            color: colorScheme.text,
                            height: 24,
                            mb: 1.5,
                        }}
                    />
                )}

                {/* Meta info */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {earnedDate && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <CalendarTodayIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                            <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>
                                {formatDate(earnedDate)}
                            </Typography>
                        </Box>
                    )}
                    {points && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <StarIcon sx={{ fontSize: 13, color: '#f59e0b' }} />
                            <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', fontWeight: 600 }}>
                                {points} pts
                            </Typography>
                        </Box>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

const BadgeCardSkeleton = () => (
    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
            <Skeleton variant="circular" width={72} height={72} sx={{ mx: 'auto', mb: 2 }} />
            <Skeleton variant="text" width="70%" sx={{ mx: 'auto', fontSize: '1rem' }} />
            <Skeleton variant="text" width="90%" sx={{ mx: 'auto', fontSize: '0.85rem' }} />
            <Skeleton variant="text" width="50%" sx={{ mx: 'auto', fontSize: '0.75rem' }} />
        </CardContent>
    </Card>
);

export default function AchievementsPage() {
    const [badges, setBadges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchBadges = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axiosInstance.get('/lms/portal/badges');
            const data = response.data;
            setBadges(data.badges || data.data || data.results || []);
        } catch (err) {
            console.error('Failed to fetch badges:', err);
            setError(err.response?.data?.message || 'Failed to load achievements.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchBadges();
    }, [fetchBadges]);

    // Stats
    const totalBadges = badges.length;
    const recentBadges = badges.filter((b) =>
        isThisMonth(b.earned_date || b.earned_at || b.created_at)
    ).length;
    const totalPoints = badges.reduce((sum, b) => sum + (b.points || b.xp || 0), 0);

    // Group by category
    const categories = {};
    badges.forEach((badge) => {
        const cat = badge.category || badge.badge_category || 'General';
        if (!categories[cat]) categories[cat] = [];
        categories[cat].push(badge);
    });
    const categoryNames = Object.keys(categories);
    const hasCategories = categoryNames.length > 1;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    borderRadius: { xs: 0, md: '0 0 24px 24px' },
                    px: { xs: 2, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <EmojiEventsIcon sx={{ fontSize: 40, color: 'white' }} />
                    <Box>
                        <Typography
                            variant="h4"
                            sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.5rem', md: '2rem' } }}
                        >
                            Achievements
                        </Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>
                            Your badges and accomplishments
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {/* Stats */}
                <Grid container spacing={2.5} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={4}>
                        <StatCard
                            icon={MilitaryTechIcon}
                            title="Total Badges"
                            value={loading ? '--' : totalBadges}
                            color="#f59e0b"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <StatCard
                            icon={LocalFireDepartmentIcon}
                            title="Earned This Month"
                            value={loading ? '--' : recentBadges}
                            color="#ef4444"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <StatCard
                            icon={StarIcon}
                            title="Total Points"
                            value={loading ? '--' : totalPoints.toLocaleString()}
                            color="#3b82f6"
                        />
                    </Grid>
                </Grid>

                {/* Error */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 3, borderRadius: '12px', fontFamily }}
                        action={
                            <Button color="inherit" size="small" onClick={fetchBadges} sx={{ fontFamily, textTransform: 'none' }}>
                                Retry
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                )}

                {/* Loading */}
                {loading ? (
                    <Grid container spacing={3}>
                        {Array.from({ length: 8 }).map((_, i) => (
                            <Grid item xs={6} sm={4} md={3} key={i}>
                                <BadgeCardSkeleton />
                            </Grid>
                        ))}
                    </Grid>
                ) : badges.length === 0 ? (
                    /* Empty State */
                    <Paper
                        sx={{
                            py: 8,
                            px: 3,
                            textAlign: 'center',
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            boxShadow: 'none',
                        }}
                    >
                        <WorkspacePremiumIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                        <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 1 }}>
                            No badges earned yet
                        </Typography>
                        <Typography sx={{ fontFamily, color: '#64748b', mb: 3 }}>
                            Complete courses and activities to earn badges and achievements!
                        </Typography>
                    </Paper>
                ) : hasCategories ? (
                    /* Grouped by category */
                    categoryNames.map((catName) => (
                        <Box key={catName} sx={{ mb: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Typography
                                    variant="h6"
                                    sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}
                                >
                                    {catName}
                                </Typography>
                                <Chip
                                    label={categories[catName].length}
                                    size="small"
                                    sx={{
                                        fontFamily,
                                        fontWeight: 600,
                                        bgcolor: `${ACCENT}15`,
                                        color: ACCENT,
                                        height: 24,
                                        fontSize: '0.75rem',
                                    }}
                                />
                            </Box>
                            <Grid container spacing={2.5}>
                                {categories[catName].map((badge, idx) => (
                                    <Grid item xs={6} sm={4} md={3} key={badge.id || badge._id || idx}>
                                        <BadgeCard badge={badge} index={idx} />
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    ))
                ) : (
                    /* Flat grid */
                    <Grid container spacing={2.5}>
                        {badges.map((badge, index) => (
                            <Grid item xs={6} sm={4} md={3} key={badge.id || badge._id || index}>
                                <BadgeCard badge={badge} index={index} />
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>
        </Box>
    );
}
